import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type {
  CustomerDetail,
  CustomerPage,
  CustomerSummary,
  DeleteAccountResult,
} from '../api/types';
import { Alert, Spinner, formatDate, money, statusLabel, statusTone } from '../components/common';
import { useAuth } from '../state/AuthContext';

/**
 * The customer book.
 *
 * Exists to answer the two questions actually asked at the counter and on the
 * phone: "who is this person" and "what did they order". Read-only by design —
 * nothing here edits an account, so there is no way to damage one from a screen
 * meant for looking things up.
 */
export function StaffCustomersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState<CustomerPage | null>(null);
  const [selected, setSelected] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (term: string, pageNumber = 1) => {
    setBusy(true);

    try {
      setPage(await api.staffCustomers(term, pageNumber));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Debounced so typing a phone number does not fire a query per digit.
    const timer = window.setTimeout(() => void load(search), 300);
    return () => window.clearTimeout(timer);
  }, [search, load]);

  async function open(customer: CustomerSummary) {
    setBusy(true);
    setError(null);

    try {
      setSelected(await api.staffCustomer(customer.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (selected) {
    return (
      <CustomerDetailView
        detail={selected}
        onBack={() => setSelected(null)}
        onRoleChanged={async () => {
          setSelected(await api.staffCustomer(selected.customer.id));
          await load(search, page?.page ?? 1);
        }}
      />
    );
  }

  return (
    <section className="page">
      <h1>Customers</h1>

      {error && <Alert>{error}</Alert>}

      <label className="field">
        <span>Search</span>
        <input
          type="search"
          value={search}
          placeholder="Name, email, or phone — 082 111 2222 works"
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>

      {!page ? (
        <Spinner label="Loading customers…" />
      ) : (
        <>
          <p className="muted small">
            {page.total} customer{page.total === 1 ? '' : 's'}
            {search ? ' matching' : ''} · showing {page.customers.length}
          </p>

          {page.customers.length === 0 && (
            <Alert tone="info">Nobody matches that. Try part of a name, or the last few digits.</Alert>
          )}

          <div className="custlist">
            {page.customers.map((c) => (
              <button key={c.id} type="button" className="custrow" disabled={busy} onClick={() => open(c)}>
                <div className="custrow__who">
                  <strong>{c.fullName}</strong>
                  <span className="muted small">
                    {c.email} · {c.phone}
                  </span>
                </div>
                <div className="custrow__stats">
                  <span>
                    <strong>{c.orderCount}</strong> order{c.orderCount === 1 ? '' : 's'}
                  </span>
                  <span>{money(c.totalSpent)}</span>
                  <span className="muted small">
                    {c.lastOrderAt ? `last ${formatDate(c.lastOrderAt)}` : 'never ordered'}
                  </span>
                  {c.cardsCompleted > 0 && (
                    <span className="pill pill--good">
                      {c.cardsCompleted} card{c.cardsCompleted === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                {c.role !== 'Customer' && <span className="pill">{c.role}</span>}
                {!c.isActive && <span className="pill pill--bad">Disabled</span>}
              </button>
            ))}
          </div>

          {page.total > page.customers.length && (
            <div className="custpager">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={page.page <= 1 || busy}
                onClick={() => void load(search, page.page - 1)}
              >
                Previous
              </button>
              <span className="muted small">Page {page.page}</span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={page.page * page.pageSize >= page.total || busy}
                onClick={() => void load(search, page.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function CustomerDetailView({
  detail,
  onBack,
  onRoleChanged,
}: {
  detail: CustomerDetail;
  onBack: () => void;
  onRoleChanged: () => Promise<void>;
}) {
  const c = detail.customer;
  const { customer: me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Closing an account is the one destructive thing on this screen, so it asks
  // for the address to be typed rather than for a dialog to be dismissed.
  const [confirmEmail, setConfirmEmail] = useState('');
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closed, setClosed] = useState<DeleteAccountResult | null>(null);

  async function closeAccount() {
    setBusy(true);
    setCloseError(null);

    try {
      setClosed(await api.staffDeleteCustomer(c.id, confirmEmail.trim()));
    } catch (e) {
      setCloseError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setRole(role: string) {
    const label = role === 'Customer' ? 'remove all staff access from' : `make ${role.toLowerCase()}`;
    if (!window.confirm(`Are you sure you want to ${label} ${c.fullName}?`)) return;

    setBusy(true);
    setRoleError(null);

    try {
      await api.staffSetRole(c.id, role);
      await onRoleChanged();
    } catch (e) {
      // The server refuses to remove the last admin — that message has to
      // reach the screen, or somebody locks themselves out of the console.
      setRoleError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page">
      <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
        ← All customers
      </button>

      <h1>{c.fullName}</h1>
      <p className="muted">
        <a href={`mailto:${c.email}`}>{c.email}</a> · <a href={`tel:${c.phone}`}>{c.phone}</a>
      </p>

      <div className="panel">
        <div className="stats">
          <div>
            <strong>{c.orderCount}</strong>
            <span className="muted small">orders</span>
          </div>
          <div>
            <strong>{money(c.totalSpent)}</strong>
            <span className="muted small">spent</span>
          </div>
          <div>
            <strong>{detail.loyaltyStamps}</strong>
            <span className="muted small">stamps</span>
          </div>
          <div>
            <strong>{detail.loyaltyRewards}</strong>
            <span className="muted small">free items waiting</span>
          </div>
          <div>
            <strong>{detail.cardsCompleted}</strong>
            <span className="muted small">cards completed</span>
          </div>
        </div>
        <p className="muted small">
          Joined {formatDate(c.joinedAt)}
          {c.lastLoginAt ? ` · last signed in ${formatDate(c.lastLoginAt)}` : ''}
          {c.marketingOptIn ? ' · opted in to marketing' : ' · not opted in to marketing'}
        </p>
      </div>

      {/* Only an admin can hand out access, and the API enforces that too —
          this is the convenient path, not the security boundary. */}
      {(me?.role === 'Admin' || me?.role === 'SuperAdmin') && (
        <div className="panel">
          <h2 className="panel__title">Access</h2>

          {roleError && <Alert>{roleError}</Alert>}

          <p className="muted small">
            Currently <strong>{c.role}</strong>.{' '}
            {c.role === 'Customer'
              ? 'Can order and collect loyalty stamps, nothing more.'
              : c.role === 'Staff'
                ? 'Can work the order board, the loyalty counter, memberships, customers and drivers.'
                : c.role === 'Admin'
                  ? 'Everything staff can do, plus settings and handing out access.'
                  : c.role === 'SuperAdmin'
                    ? 'Everything an admin can do, plus closing accounts.'
                    : 'Delivers orders. Sees only their own jobs.'}
          </p>

          <div className="rolebtns">
            {(me?.role === 'SuperAdmin'
              ? ['Customer', 'Staff', 'Admin', 'SuperAdmin']
              : ['Customer', 'Staff', 'Admin']
            ).map((role) => (
              <button
                key={role}
                type="button"
                className={c.role === role ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                disabled={busy || c.role === role || c.id === me?.id}
                onClick={() => setRole(role)}
              >
                {c.role === role ? `${role} ✓` : `Make ${role.toLowerCase()}`}
              </button>
            ))}
          </div>

          {c.id === me?.id && (
            <p className="muted small">
              You cannot change your own access here — ask another admin, so nobody locks themselves
              out by accident.
            </p>
          )}

          <p className="muted small">
            Driver access is not granted here. Someone becomes a driver by applying and being
            approved on the Drivers screen, which is where their documents are checked.
          </p>
        </div>
      )}

      {/* Super admin only. The API enforces it as well — this is the convenient
          path, not the security boundary. */}
      {/* me.role comes from /api/auth/me, i.e. the database, so it is current
          even when the access token still says something older. The API makes
          the same check against live data. */}
      {me?.role === 'SuperAdmin' && c.id !== me?.id && (
        <div className="panel panel--danger">
          <h2 className="panel__title">Close this account</h2>

          {closed ? (
            <Alert tone="success">
              {closed.removed
                ? `${closed.email} has been deleted. There was no order history, so nothing was kept.`
                : `${closed.email} has been erased. ${closed.ordersRetained} order${
                    closed.ordersRetained === 1 ? '' : 's'
                  } stayed on the books with the personal details stripped out.`}
              {closed.documentsErased > 0 &&
                ` ${closed.documentsErased} identity document${
                  closed.documentsErased === 1 ? '' : 's'
                } were deleted.`}
            </Alert>
          ) : (
            <>
              {closeError && <Alert>{closeError}</Alert>}

              <p className="muted small">
                This cannot be undone. Their name, phone number, e-mail, saved addresses and any
                identity documents are destroyed, and they are signed out everywhere.
                {c.orderCount > 0
                  ? ` Their ${c.orderCount} order${c.orderCount === 1 ? '' : 's'} stay on the books
                     so the takings still add up, but will no longer be attached to a person.`
                  : ' They have no orders, so the account will be removed entirely.'}
              </p>

              <label className="field">
                <span>
                  Type <strong>{c.email}</strong> to confirm
                </span>
                <input
                  type="email"
                  value={confirmEmail}
                  autoComplete="off"
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={c.email}
                />
              </label>

              <button
                type="button"
                className="btn btn--danger"
                disabled={busy || confirmEmail.trim().toLowerCase() !== c.email.toLowerCase()}
                onClick={closeAccount}
              >
                {busy ? 'Closing…' : 'Close this account permanently'}
              </button>
            </>
          )}
        </div>
      )}

      {detail.addresses.length > 0 && (
        <div className="panel">
          <h2 className="panel__title">Saved addresses</h2>
          {detail.addresses.map((a) => (
            <p key={a.id} className="muted small">
              <strong>{a.label}</strong>
              {a.isDefault ? ' (default)' : ''} — {a.address}
            </p>
          ))}
        </div>
      )}

      <h2 className="section-heading">Orders</h2>

      {detail.orders.length === 0 && <Alert tone="info">This customer has not ordered yet.</Alert>}

      {detail.orders.map((o) => (
        <div key={o.id} className="panel custorder">
          <div className="custorder__head">
            <strong>{o.orderNumber}</strong>
            <span className={`pill ${statusTone(o.status)}`}>{statusLabel(o.status)}</span>
          </div>

          <p className="muted small">
            {formatDate(o.placedAt)} · {o.fulfilment}
            {o.deliveredTo ? ` to ${o.deliveredTo}` : ''}
            {/* Placed while the shop was closed — worth seeing here, because it
                explains a gap between ordering and it being made. */}
            {o.scheduledFor ? ` · pre-order for ${formatDate(o.scheduledFor)}` : ''}
          </p>

          <ul className="custorder__items">
            {o.items.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>

          <p className="custorder__total">
            <strong>{money(o.total)}</strong>
          </p>
        </div>
      ))}
    </section>
  );
}
